import type { ComponentProps, KeyboardEvent, MouseEvent } from 'react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Spinner } from '@/components/ui/spinner'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { CircleAlertIcon } from 'lucide-react'

import { tv, VariantProps } from 'tailwind-variants'

import { useCellSnapshot } from '@/features/rundown/data/cells.store'

import { useColumnWidth } from '../column/column-layout'
import { gridSaveActions, isOwnCellUpdate, saveKeys } from '../grid/grid.save'
import { useSaveIndicator } from '../grid/grid.save.hooks'
import {
  gridSelectionActions,
  gridState,
  gridStateActions,
} from '../grid/grid.store'

import { getCellActions } from './cell.actions'
import { useCellKey } from './cell.context'
import {
  useCellColumn,
  useCellIsEditing,
  useCellIsInPrivateNotesColumn,
  useCellIsReadOnly,
  useCellIsSelected,
} from './cell.hooks'
import type { CellDomSchema } from './cell.schema'

const frameStyle = tv({
  base: [
    'relative', // Position inner toolbar
    'rounded-sm',
    'w-(--col-width) min-w-(--col-width)', // Column width variable set in CellFrame
    'bg-(--cue-cell-bg-color)', // Background color inherited from cue context
    'outline-2 outline-transparent', // base outline (invisible)
    'focus:outline-neutral-200', // focus outline
  ],
  variants: {
    isSelected: {
      true: [
        'outline-neutral-500', // selection outline
      ],
      false: [
        'hover:outline-neutral-500', // hover outline
      ],
    },
    isEditing: {
      true: [
        // Editor handles editing outline
        'bg-(--cue-cell-bg-color)/80', // Override bg with inherited bg color
      ],
    },
    isInPrivateNotesColumn: {
      true: 'bg-(--cue-cell-bg-color)/75', // CueCell color inherited from CueItem
    },
  },
})

export function CellFrame({ children }: ComponentProps<'div'>) {
  const cellKey = useCellKey()
  const column = useCellColumn()
  const width = useColumnWidth(column)

  const isSelected = useCellIsSelected()
  const isEditing = useCellIsEditing()
  const isInPrivateNotesColumn = useCellIsInPrivateNotesColumn()
  const isReadOnly = useCellIsReadOnly()

  const { select, deselect, startEditMode } = getCellActions(cellKey)

  // #region ============== EVENTS

  const onClick = () => {
    if (isEditing) return
    if (isSelected) return

    select()
  }

  const onContextMenu = (e: MouseEvent) => {
    if (isEditing) return

    // Called after isEditing so that native contextmenu works in editing mode
    e.preventDefault()

    if (!isSelected) {
      select() // Avoid confusion with shortcuts on one cell vs contextmenu on another
    }

    gridStateActions.contextMenu.open({
      type: 'cell',
      cellKey: cellKey,
      isReadOnly: isReadOnly,
      x: e.clientX,
      y: e.clientY,
    })
  }

  const onMouseDown = (e: MouseEvent) => {
    if (isEditing) return
    if (e.detail < 2) return
    // Safari selects surrounding blocks when double-clicking off text, painting rows blue
    if (!hasOwnText(e.target)) e.preventDefault()
  }

  const onDoubleClick = (e: MouseEvent) => {
    e.preventDefault()
    if (isEditing) return
    if (isReadOnly) return

    // Enable editing and pass event for focus feature
    startEditMode(e)
  }

  const onKeyDown = (e: KeyboardEvent) => {
    const isPlainKey = !e.ctrlKey && !e.shiftKey && !e.altKey && !e.metaKey

    // Escape from a child (e.g. a task checkbox) returns focus to the cell
    if (
      e.key === 'Escape' &&
      e.target !== e.currentTarget &&
      !isEditing &&
      e.currentTarget instanceof HTMLElement
    ) {
      e.preventDefault()
      gridSelectionActions.focus(e.currentTarget)
      return
    }

    // Enter on a task checkbox edits the cell, like Enter on the cell. Other
    // children, e.g. buttons, keep their native Enter.
    if (
      e.key === 'Enter' &&
      isPlainKey &&
      e.target instanceof HTMLInputElement &&
      e.target.type === 'checkbox'
    ) {
      onKeyEnter(e)
      return
    }

    // Only trigger if target is frame, not children
    if (e.target !== e.currentTarget) return

    if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault() // TODO: Temp. focus trap to avoid getting stuck in broken cell title editable input
      return
    }
    if (e.key === 'Enter' && isPlainKey) {
      onKeyEnter(e)
      return
    }
    if (e.key === 'Escape') {
      onKeyEscape(e)
      return
    }
  }

  const onKeyEnter = (e: KeyboardEvent) => {
    if (!(e.target instanceof HTMLElement)) return
    e.preventDefault()

    if (!isSelected) {
      select()
    }

    if (isEditing) return
    if (isReadOnly) return

    startEditMode(e)
  }

  const onKeyEscape = (e: KeyboardEvent) => {
    if (!(e.target instanceof HTMLElement)) return
    e.preventDefault()

    if (isEditing) return

    e.target.blur()
    deselect()
  }

  // #endregion

  return (
    <div
      key={cellKey}
      role="cell" // Semantic cell role (interpreted as 'gridcell') & structural ARIA hint for toolbar
      {
        // Type-safe HTML attributes
        ...({
          'data-cell-key': cellKey,
          'aria-selected': isSelected,
        } satisfies CellDomSchema)
      }
      className={frameStyle({
        isInPrivateNotesColumn: isInPrivateNotesColumn,
        isSelected: isSelected,
        isEditing: isEditing,
      })}
      style={{
        // Set CSS variables
        ['--col-width' as string]: `${width}px`,
      }}
      tabIndex={isSelected ? 0 : -1} // Roving tabIndex
      onClick={onClick}
      onMouseDown={onMouseDown}
      onContextMenu={onContextMenu}
      onDoubleClick={onDoubleClick}
      onKeyDown={onKeyDown}
    >
      {children}

      <CellSaveStatusFrame />

      <CellExternalStatusFrame />
    </div>
  )
}

function hasOwnText(target: EventTarget) {
  if (!(target instanceof Element)) return false
  return Array.from(target.childNodes).some(
    (node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim(),
  )
}

function CellSaveStatusFrame() {
  const cellKey = useCellKey()
  const column = useCellColumn()

  const indicator = useSaveIndicator(cellKey, column.id)
  if (!indicator) return null

  const saveKey =
    indicator.payload.kind === 'columnOptions'
      ? saveKeys.column(column.id)
      : saveKeys.cell(cellKey)

  switch (indicator.status) {
    case 'confirmed':
      // Saved, only waiting for the listener to deliver it
      return null

    case 'pending':
      return (
        <StatusFrame variant="pending">
          <Tooltip>
            <TooltipTrigger
              aria-label="Saving..."
              render={<Spinner />}
            ></TooltipTrigger>
            <TooltipContent>Saving&hellip;</TooltipContent>
          </Tooltip>
        </StatusFrame>
      )

    case 'error':
      return (
        <StatusFrame variant="error">
          <DropdownMenu>
            <DropdownMenuTrigger
              onClick={(event) => {
                // Prevent selecting cell and showing toolbar
                event.stopPropagation()
              }}
              render={
                <Button
                  variant="ghost"
                  size="icon"
                  style={
                    { '--muted': 'var(--color-red-800)' } as React.CSSProperties // Override hover and active with error color
                  }
                >
                  <CircleAlertIcon />
                </Button>
              }
            />
            <DropdownMenuContent finalFocus={false}>
              <DropdownMenuGroup>
                <DropdownMenuLabel>Unsaved changes</DropdownMenuLabel>

                <DropdownMenuItem
                  onClick={() => gridSaveActions.retry(saveKey)}
                >
                  Retry
                </DropdownMenuItem>

                <DropdownMenuItem
                  variant="destructive"
                  onClick={() => gridSaveActions.abandon(saveKey, cellKey)}
                >
                  Abandon
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </StatusFrame>
      )

    default: {
      const _exhaustive: never = indicator
      return _exhaustive
    }
  }
}

const statusFrameStyle = tv({
  slots: {
    base: 'pointer-events-none',
    border: 'absolute inset-0 rounded-sm ring-3 ring-inset',
    label: [
      'absolute top-0 right-0 size-8',
      'flex items-center justify-center',
      'rounded-tr-md rounded-bl-md',
      'pointer-events-auto',
    ],
  },
  variants: {
    variant: {
      pending: {
        border: 'ring-neutral-700',
        label: 'bg-neutral-700',
      },
      error: {
        border: 'ring-red-500',
        label: 'bg-red-500',
      },
      externalUpdate: {
        border: 'ring-yellow-500',
        label: 'bg-transparent',
      },
    },
  },
})

export function StatusFrame({
  variant,
  children,
  className,
  ...props
}: Required<VariantProps<typeof statusFrameStyle>> &
  React.ComponentProps<'div'>) {
  const { base, border, label } = statusFrameStyle({ variant })

  return (
    <div
      className={base({ className })}
      {...props}
    >
      <div className={border()} />
      {children && <div className={label()}>{children}</div>}
    </div>
  )
}

function CellExternalStatusFrame() {
  const cellKey = useCellKey()
  const cell = useCellSnapshot()

  const updatedAtMs = cell?.updatedAt?.getTime() ?? 0

  // Version seen on the previous render, and the external version to flash
  const [seenUpdatedAtMs, setSeenUpdatedAtMs] = useState(updatedAtMs)
  const [flashUpdatedAtMs, setFlashUpdatedAtMs] = useState<number | null>(null)

  // Classify each new version once, when it arrives. Adjusting state during
  // render: https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  if (updatedAtMs !== seenUpdatedAtMs) {
    setSeenUpdatedAtMs(updatedAtMs)

    const isExternal =
      updatedAtMs > seenUpdatedAtMs &&
      // Our own save, possibly delivered long after editing ended
      !isOwnCellUpdate(cellKey, new Date(updatedAtMs)) &&
      // The editor handles updates mid-session as conflicts
      gridState.editingCell !== cellKey

    if (isExternal) setFlashUpdatedAtMs(updatedAtMs)
  }

  if (flashUpdatedAtMs === null) return null

  return (
    <StatusFrame
      key={flashUpdatedAtMs} // Restart animation on every new external update
      variant="externalUpdate"
      className="animate-fade-out"
      onAnimationEnd={() => setFlashUpdatedAtMs(null)}
    >
      {/* Indicator dot */}
      <div className="size-1.25 rounded-full bg-yellow-500" />
    </StatusFrame>
  )
}
